<template>
    <div class="loading-container" :class="containerClass">
        <div v-if="type === 'spinner'" class="spinner" :class="spinnerClass">
            <div class="spinner-circle"></div>
        </div>

        <div v-else-if="type === 'dots'" class="dots-loader" :class="dotsClass">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
        </div>

        <div v-else-if="type === 'pulse'" class="pulse-loader" :class="pulseClass">
            <div class="pulse-circle"></div>
        </div>

        <div v-else-if="type === 'bars'" class="bars-loader" :class="barsClass">
            <div class="bar"></div>
            <div class="bar"></div>
            <div class="bar"></div>
            <div class="bar"></div>
        </div>

        <p v-if="message" class="loading-message">{{ message }}</p>
    </div>
</template>

<script setup lang="ts">
interface Props {
    type?: 'spinner' | 'dots' | 'pulse' | 'bars'
    size?: 'sm' | 'md' | 'lg' | 'xl'
    message?: string
    overlay?: boolean
    center?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    type: 'spinner',
    size: 'md',
    overlay: false,
    center: false
})

const containerClass = computed(() => ({
    'loading-overlay': props.overlay,
    'loading-center': props.center,
    [`loading-${props.size}`]: true
}))

const spinnerClass = computed(() => ({
    [`spinner-${props.size}`]: true
}))

const dotsClass = computed(() => ({
    [`dots-${props.size}`]: true
}))

const pulseClass = computed(() => ({
    [`pulse-${props.size}`]: true
}))

const barsClass = computed(() => ({
    [`bars-${props.size}`]: true
}))
</script>

<script lang="ts">
import { computed } from 'vue'
</script>

<style scoped>
/* Loading Container */
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
}

.loading-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
    z-index: 50;
}

.loading-center {
    min-height: 16rem;
}

/* Size variants */
.loading-sm {
    gap: 0.5rem;
}

.loading-md {
    gap: 1rem;
}

.loading-lg {
    gap: 1.5rem;
}

.loading-xl {
    gap: 2rem;
}

/* Spinner Loader */
.spinner {
    position: relative;
}

.spinner-circle {
    border: 4px solid #e5e7eb;
    border-top-color: var(--color-primary-500);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.spinner-sm .spinner-circle {
    width: 1.5rem;
    height: 1.5rem;
    border-width: 2px;
}

.spinner-md .spinner-circle {
    width: 2rem;
    height: 2rem;
}

.spinner-lg .spinner-circle {
    width: 3rem;
    height: 3rem;
    border-width: 4px;
}

.spinner-xl .spinner-circle {
    width: 4rem;
    height: 4rem;
    border-width: 4px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Dots Loader */
.dots-loader {
    display: flex;
    gap: 0.5rem;
}

.dot {
    background-color: var(--color-primary-500);
    border-radius: 50%;
    animation: dots-bounce 1.4s ease-in-out infinite both;
}

.dots-sm .dot {
    width: 0.5rem;
    height: 0.5rem;
}

.dots-md .dot {
    width: 0.75rem;
    height: 0.75rem;
}

.dots-lg .dot {
    width: 1rem;
    height: 1rem;
}

.dots-xl .dot {
    width: 1.25rem;
    height: 1.25rem;
}

.dot:nth-child(1) {
    animation-delay: -0.32s;
}

.dot:nth-child(2) {
    animation-delay: -0.16s;
}

@keyframes dots-bounce {

    0%,
    80%,
    100% {
        transform: scale(0);
    }

    40% {
        transform: scale(1);
    }
}

/* Pulse Loader */
.pulse-loader {
    position: relative;
}

.pulse-circle {
    background-color: var(--color-primary-500);
    border-radius: 50%;
    animation: pulse-scale 1s ease-in-out infinite;
}

.pulse-sm .pulse-circle {
    width: 1.5rem;
    height: 1.5rem;
}

.pulse-md .pulse-circle {
    width: 2rem;
    height: 2rem;
}

.pulse-lg .pulse-circle {
    width: 3rem;
    height: 3rem;
}

.pulse-xl .pulse-circle {
    width: 4rem;
    height: 4rem;
}

@keyframes pulse-scale {
    0% {
        transform: scale(0);
        opacity: 1;
    }

    100% {
        transform: scale(1);
        opacity: 0;
    }
}

/* Bars Loader */
.bars-loader {
    display: flex;
    gap: 0.25rem;
    align-items: flex-end;
}

.bar {
    background-color: var(--color-primary-500);
    animation: bars-stretch 1.2s ease-in-out infinite;
}

.bars-sm .bar {
    width: 0.25rem;
    height: 16px;
}

.bars-md .bar {
    width: 0.25rem;
    height: 24px;
}

.bars-lg .bar {
    width: 0.5rem;
    height: 32px;
}

.bars-xl .bar {
    width: 0.5rem;
    height: 40px;
}

.bar:nth-child(1) {
    animation-delay: -1.1s;
}

.bar:nth-child(2) {
    animation-delay: -1.0s;
}

.bar:nth-child(3) {
    animation-delay: -0.9s;
}

.bar:nth-child(4) {
    animation-delay: -0.8s;
}

@keyframes bars-stretch {

    0%,
    40%,
    100% {
        transform: scaleY(0.4);
    }

    20% {
        transform: scaleY(1.0);
    }
}

/* Loading Message */
.loading-message {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-normal);
    color: rgb(75 85 99);
    text-align: center;
}

/* Enhanced animations with green accent */
.spinner-circle {
    border-top-color: var(--color-primary-500);
    border-right-color: var(--color-primary-300);
}

.dot {
    background: linear-gradient(45deg, var(--color-primary-500), var(--color-primary-400));
}

.pulse-circle {
    background: radial-gradient(circle, var(--color-primary-500), var(--color-primary-400));
}

.bar {
    background: linear-gradient(to top, var(--color-primary-600), var(--color-primary-400));
}

/* Overlay improvements */
.loading-overlay {
    backdrop-filter: blur(4px);
    background: rgba(255, 255, 255, 0.8);
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
    .loading-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .loading-message {
        color: rgb(209 213 219);
    }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {

    .spinner-circle,
    .dot,
    .pulse-circle,
    .bar {
        animation-duration: 2s;
    }
}
</style>